import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Smartphone, Key, MessageSquare, Calendar, Bell } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MobileGuest = () => {
  const { selectedProperty } = useOutletContext();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedProperty) {
      setLoading(false);
    }
  }, [selectedProperty]);

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="mobile-guest-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Mobile Guest</h1>
          <p className="text-slate-600 mt-1">Guest mobile app features and API</p>
        </div>
      </div>

      <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Smartphone className="text-blue-600 mt-1" size={32} />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2 text-lg">Guest Mobile App</h3>
              <p className="text-sm text-blue-800">
                Provide guests with a seamless mobile experience from booking to check-out.
                All features are accessible via REST API for native iOS/Android app integration.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key size={20} />
              Digital Room Key
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Access digital door codes directly from mobile app
              </p>
              <div className="p-4 bg-emerald-50 rounded-lg text-center">
                <div className="text-3xl font-bold text-emerald-600 mb-1">6-Digit Code</div>
                <p className="text-xs text-slate-600">Valid 4hrs before check-in</p>
              </div>
              <div className="text-xs text-slate-500">
                <strong>API:</strong> GET /mobile/guest/digital-key
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar size={20} />
              Current Stay Info
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                View reservation details and property information
              </p>
              <ul className="text-sm space-y-1 text-slate-600">
                <li>• Check-in/check-out times</li>
                <li>• Room number and amenities</li>
                <li>• Property contact info</li>
                <li>• Booking confirmation</li>
              </ul>
              <div className="text-xs text-slate-500">
                <strong>API:</strong> GET /mobile/guest/current-stay
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell size={20} />
              Service Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Request services directly from mobile app
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-blue-50 rounded text-center text-xs">Housekeeping</div>
                <div className="p-2 bg-purple-50 rounded text-center text-xs">Maintenance</div>
                <div className="p-2 bg-emerald-50 rounded text-center text-xs">Amenities</div>
                <div className="p-2 bg-amber-50 rounded text-center text-xs">Concierge</div>
              </div>
              <div className="text-xs text-slate-500">
                <strong>API:</strong> POST /mobile/guest/service-request
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare size={20} />
              Chat with Front Desk
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Real-time messaging with property staff
              </p>
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-start gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs">
                    G
                  </div>
                  <div className="flex-1 bg-white p-2 rounded-lg text-xs">
                    Hi, what time is breakfast served?
                  </div>
                </div>
                <div className="flex items-start gap-2 justify-end">
                  <div className="flex-1 bg-blue-500 text-white p-2 rounded-lg text-xs text-right">
                    7 AM - 10 AM daily!
                  </div>
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs">
                    S
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-500">
                <strong>API:</strong> GET /mobile/guest/messages
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Additional Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg">
              <h3 className="font-semibold mb-2">📋 Pre-Check-in</h3>
              <p className="text-sm text-slate-600">
                Complete check-in formalities before arrival
              </p>
            </div>

            <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
              <h3 className="font-semibold mb-2">🎯 Amenities Guide</h3>
              <p className="text-sm text-slate-600">
                Browse property amenities and services
              </p>
            </div>

            <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg">
              <h3 className="font-semibold mb-2">💳 Mobile Payments</h3>
              <p className="text-sm text-slate-600">
                Secure in-app payment processing
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Available API Endpoints</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="font-mono text-slate-600">GET /mobile/guest/reservations</span>
              <Badge>Ready</Badge>
            </div>
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="font-mono text-slate-600">GET /mobile/guest/current-stay</span>
              <Badge>Ready</Badge>
            </div>
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="font-mono text-slate-600">GET /mobile/guest/digital-key</span>
              <Badge>Ready</Badge>
            </div>
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="font-mono text-slate-600">POST /mobile/guest/service-request</span>
              <Badge>Ready</Badge>
            </div>
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="font-mono text-slate-600">GET /mobile/guest/messages</span>
              <Badge>Ready</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MobileGuest;
